/**
 * EASING — the brand's four curves as JS functions (owner S3).
 *
 * Ported from src/dev/reference/aesthetic.jsx §13 bezierEase (L2269–2283): the
 * same polynomial form and the same Newton step on x, with a bisection
 * fallback added for the cases Newton cannot settle (flat slope, or not
 * converged after a few steps). The result matches CSS cubic-bezier() to
 * within 1e-7 on the brand curves (checked against a 200-step bisection).
 *
 *   settle   (.22,1,.36,1)    arrivals: panels, cards, indicators
 *   drift    (.45,0,.2,1)     camera moves, scene cross-fades, reveals
 *   leave    (.4,0,1,1)       exits accelerate away (at 0.7 of the entry duration)
 *   breathe  (.37,0,.63,1)    ambient loops only
 *
 * Dependency-free at runtime, so Node scripts can import this file directly.
 * The only import is type-only (erased); it makes tsc fail if the curves or
 * durations here ever drift from tokens.motion in ./tokens.ts.
 */
import type { EaseName } from "@/contracts/map";
import type { Tokens } from "./tokens";

export type EaseFn = (t: number) => number;

const clamp01 = (v: number): number => (v <= 0 ? 0 : v >= 1 ? 1 : v);

/** Cubic-bezier easing identical to CSS cubic-bezier(x1,y1,x2,y2). */
export function bezierEase(x1: number, y1: number, x2: number, y2: number): EaseFn {
  // Same coefficient form as the reference: B(t) = ((A·t + B)·t + C)·t
  const A = (a: number, b: number) => 1 - 3 * b + 3 * a;
  const B = (a: number, b: number) => 3 * b - 6 * a;
  const C = (a: number) => 3 * a;
  const calc = (t: number, a: number, b: number) => ((A(a, b) * t + B(a, b)) * t + C(a)) * t;
  const slope = (t: number, a: number, b: number) => 3 * A(a, b) * t * t + 2 * B(a, b) * t + C(a);
  // CSS clamps the x control points to [0, 1]; that keeps x(t) monotonic.
  const ax1 = clamp01(x1);
  const ax2 = clamp01(x2);
  if (ax1 === y1 && ax2 === y2) return (x) => clamp01(x);

  const EPS = 1e-9;
  const solveT = (x: number): number => {
    // Newton on x(t) = x, starting at t = x (the reference's 6 steps, plus two).
    let t = x;
    for (let i = 0; i < 8; i++) {
      const err = calc(t, ax1, ax2) - x;
      if (Math.abs(err) < EPS) return t;
      const s = slope(t, ax1, ax2);
      if (Math.abs(s) < 1e-6) break;
      t -= err / s;
    }
    // Bisection fallback: x(t) is monotonic on [0, 1].
    let lo = 0;
    let hi = 1;
    t = x;
    for (let i = 0; i < 60; i++) {
      const v = calc(t, ax1, ax2);
      if (Math.abs(v - x) < EPS) break;
      if (v < x) lo = t;
      else hi = t;
      t = (lo + hi) / 2;
    }
    return t;
  };

  return (x) => {
    if (!(x > 0)) return 0; // also catches NaN
    if (x >= 1) return 1;
    return calc(clamp01(solveT(x)), y1, y2);
  };
}

/** Parses "cubic-bezier(.22,1,.36,1)" into its four numbers. null when the string is not a cubic-bezier(). */
export function parseCubicBezier(css: string): [number, number, number, number] | null {
  const m = /^\s*cubic-bezier\(\s*([^)]+)\)\s*$/i.exec(css);
  if (!m) return null;
  const n = m[1].split(",").map((s) => Number(s.trim()));
  return n.length === 4 && n.every(Number.isFinite) ? [n[0], n[1], n[2], n[3]] : null;
}

/* ── the brand curves ───────────────────────────────────────────────── */

// Literal copies of tokens.motion.ease. The type-only check below fails tsc if they drift.
const CURVES = {
  settle: "cubic-bezier(.22,1,.36,1)",
  drift: "cubic-bezier(.45,0,.2,1)",
  leave: "cubic-bezier(.4,0,1,1)",
  breathe: "cubic-bezier(.37,0,.63,1)",
} as const;
const _curvesMatchTokens: Tokens["motion"]["ease"] = CURVES;
void _curvesMatchTokens;

/** CSS timing functions, for style={{ transition }} and WAAPI. */
export const EASE_CSS: Record<EaseName, string> = { ...CURVES, linear: "linear" };

const fromCss = (css: string): EaseFn => {
  const p = parseCubicBezier(css);
  return p ? bezierEase(p[0], p[1], p[2], p[3]) : clamp01;
};

/** settle (.22,1,.36,1) arrivals · drift (.45,0,.2,1) camera/reveals · leave (.4,0,1,1) exits · breathe (.37,0,.63,1) ambient. */
export const EASE: Record<EaseName, EaseFn> = {
  settle: fromCss(CURVES.settle),
  drift: fromCss(CURVES.drift),
  leave: fromCss(CURVES.leave),
  breathe: fromCss(CURVES.breathe),
  linear: clamp01,
};

/* ── durations ──────────────────────────────────────────────────────── */

const DURATIONS = { instant: 120, quick: 240, calm: 420, unhurried: 640, scene: 1100, ambient: 9000 } as const;
const _durationsMatchTokens: Tokens["motion"]["duration"] = DURATIONS;
const STAGGER = 40 as const;
const _staggerMatchesTokens: Tokens["motion"]["stagger"] = STAGGER;
void _durationsMatchTokens;
void _staggerMatchesTokens;

/** Durations in ms (tokens.motion.duration) + stagger. */
export const DUR = { ...DURATIONS, stagger: STAGGER } as const;

/** Exits take seven tenths of the entrance: exitMs(DUR.calm) → 294. */
export const EXIT_RATIO = 0.7;
export function exitMs(enterMs: number): number {
  return Math.round(enterMs * EXIT_RATIO);
}

/* ── lookup ─────────────────────────────────────────────────────────── */

const CSS_KEYWORDS: Record<string, string> = {
  ease: "cubic-bezier(.25,.1,.25,1)",
  "ease-in": "cubic-bezier(.42,0,1,1)",
  "ease-out": "cubic-bezier(0,0,.58,1)",
  "ease-in-out": "cubic-bezier(.42,0,.58,1)",
};
const cache = new Map<string, EaseFn>();

export const isEaseName = (s: unknown): s is EaseName => typeof s === "string" && Object.prototype.hasOwnProperty.call(EASE, s);

/**
 * A unit-free easing function (0..1 → 0..1) by name.
 * Accepts a brand curve name, a CSS keyword ("ease-out", "linear") or a
 * "cubic-bezier(a,b,c,d)" string. Anything else, including undefined, gives
 * `fallback` (default "drift", the camera's default curve).
 */
export function easeBy(name?: EaseName | (string & {}) | null, fallback: EaseName = "drift"): EaseFn {
  if (name == null) return EASE[fallback];
  if (isEaseName(name)) return EASE[name];
  const key = name.trim().toLowerCase();
  const hit = cache.get(key);
  if (hit) return hit;
  const css = CSS_KEYWORDS[key] ?? key;
  const p = parseCubicBezier(css);
  if (!p) return EASE[fallback];
  const fn = bezierEase(p[0], p[1], p[2], p[3]);
  cache.set(key, fn);
  return fn;
}
